import {
  Category,
  Product,
  Service,
  Staff,
} from '@/components/QuickAppointment/types';
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

interface QuickAppointmentState {
  open: boolean;
  categories: Category[];
  services: Record<string, Service[]>;
  products: Record<string, Product[]>;
  staffs: Record<string, Staff[]>;
  category_id?: string;
  service_id?: string;
  step: 1 | 2;
  cart: {
    services: Record<string, { data: Service; count: number }>;
    products: Record<string, { data: Product; count: number }>;
    extras: Record<string, { data: Service; count: number }>;
    staff?: Staff;
    service_ids: string[];
    product_ids: string[];
    extra_ids: string[];
    totalPrice: number;
  };
}

const initialState: QuickAppointmentState = {
  open: false,
  categories: [],
  services: {},
  products: {},
  staffs: {},
  category_id: undefined,
  service_id: undefined,
  step: 1,
  cart: {
    services: {},
    products: {},
    extras: {},
    staff: undefined,
    service_ids: [],
    product_ids: [],
    extra_ids: [],
    totalPrice: 0,
  },
};

const quickAppointment = createSlice({
  name: 'quick_appointment',
  initialState,
  reducers: {
    setOpen: (state: QuickAppointmentState) => {
      if (state.open) {
        state.cart = {
          services: {},
          products: {},
          extras: {},
          staff: undefined,
          service_ids: [],
          product_ids: [],
          extra_ids: [],
          totalPrice: 0,
        };
      }
      state.open = !state.open;
      state.step = 1;
    },
    setStep: (state: QuickAppointmentState, payload: PayloadAction<1 | 2>) => {
      state.step = payload.payload;
    },
    setInitialData: (
      state: QuickAppointmentState,
      payload: PayloadAction<{
        categories: Category[];
        services: Service[];
        products: Product[];
        staffs: Staff[];
      }>,
    ) => {
      state.services = {};
      state.products = {};
      state.staffs = {};
      state.services['0'] = [];
      payload.payload.services.forEach((service) => {
        if (!state.services[service.category_id]) {
          state.services[service.category_id] = [];
        }
        state.services['0'].push(service);
        state.services[service.category_id].push(service);
        state.products[service.id] = payload.payload.products.filter(
          (product) =>
            !product.service_ids || product.service_ids.includes(service.id),
        );
        state.staffs[service.id] = payload.payload.staffs;
      });
      state.categories = [
        { id: '0', name: 'Tout' },
        ...payload.payload.categories,
      ];
      state.category_id = '0';
      state.service_id = state.services[state.category_id]?.[0]?.id;
      state.step = 1;
    },
    setActiveCategory: (
      state: QuickAppointmentState,
      payload: PayloadAction<string>,
    ) => {
      state.category_id = payload.payload;
      state.service_id = state.services[payload.payload]?.[0]?.id;
    },
    setActiveService: (
      state: QuickAppointmentState,
      payload: PayloadAction<string>,
    ) => {
      if (state.service_id !== payload.payload) {
        state.service_id = payload.payload;
      }
    },
    setService: (
      state: QuickAppointmentState,
      payload: PayloadAction<Service>,
    ) => {
      if (!state.cart.services[payload.payload.id]) {
        state.cart.services[payload.payload.id] = {
          count: 1,
          data: payload.payload,
        };
        state.cart.service_ids.push(payload.payload.id);
      } else {
        state.cart.services[payload.payload.id].count += 1;
      }
      state.cart.totalPrice += parseFloat(payload.payload.price);
    },
    setProduct: (
      state: QuickAppointmentState,
      payload: PayloadAction<Product>,
    ) => {
      if (!state.cart.products[payload.payload.id]) {
        state.cart.products[payload.payload.id] = {
          count: 1,
          data: payload.payload,
        };
        state.cart.product_ids.push(payload.payload.id);
      } else {
        state.cart.products[payload.payload.id].count += 1;
      }
      state.cart.totalPrice += parseFloat(payload.payload.sell_price);
    },
    setExtra: (
      state: QuickAppointmentState,
      payload: PayloadAction<Service>,
    ) => {
      if (!state.cart.extras[payload.payload.id]) {
        state.cart.extras[payload.payload.id] = {
          count: 1,
          data: payload.payload,
        };
        state.cart.extra_ids.push(payload.payload.id);
      } else {
        state.cart.extras[payload.payload.id].count += 1;
      }
      state.cart.totalPrice += parseFloat(payload.payload.price);
    },
    setStaff: (state: QuickAppointmentState, payload: PayloadAction<Staff>) => {
      state.cart.staff = payload.payload;
    },
    removeService: (
      state: QuickAppointmentState,
      payload: PayloadAction<string>,
    ) => {
      if (state.cart.services[payload.payload]) {
        state.cart.totalPrice -= parseFloat(
          state.cart.services[payload.payload].data.price,
        );
        if (state.cart.services[payload.payload].count > 1) {
          state.cart.services[payload.payload].count -= 1;
        } else {
          delete state.cart.services[payload.payload];
          state.cart.service_ids = state.cart.service_ids.filter(
            (id) => id !== payload.payload,
          );
        }
      }
    },
    removeProduct: (
      state: QuickAppointmentState,
      payload: PayloadAction<string>,
    ) => {
      if (state.cart.products[payload.payload]) {
        state.cart.totalPrice -= parseFloat(
          state.cart.products[payload.payload].data.sell_price,
        );
        if (state.cart.products[payload.payload].count > 1) {
          state.cart.products[payload.payload].count -= 1;
        } else {
          delete state.cart.products[payload.payload];
          state.cart.product_ids = state.cart.product_ids.filter(
            (id) => id !== payload.payload,
          );
        }
      }
    },
    removeExtra: (
      state: QuickAppointmentState,
      payload: PayloadAction<string>,
    ) => {
      if (state.cart.extras[payload.payload]) {
        state.cart.totalPrice -= parseFloat(
          state.cart.extras[payload.payload].data.price,
        );
        if (state.cart.extras[payload.payload].count > 1) {
          state.cart.extras[payload.payload].count -= 1;
        } else {
          delete state.cart.extras[payload.payload];
          state.cart.extra_ids = state.cart.extra_ids.filter(
            (id) => id !== payload.payload,
          );
        }
      }
    },
    removeStaff: (state: QuickAppointmentState) => {
      state.cart.staff = undefined;
    },
    clearService: (
      state: QuickAppointmentState,
      payload: PayloadAction<string>,
    ) => {
      if (state.cart.services[payload.payload]) {
        state.cart.totalPrice -=
          parseFloat(state.cart.services[payload.payload].data.price) *
          state.cart.services[payload.payload].count;
        delete state.cart.services[payload.payload];
        state.cart.service_ids = state.cart.service_ids.filter(
          (id) => id !== payload.payload,
        );
      }
    },
    clearProduct: (
      state: QuickAppointmentState,
      payload: PayloadAction<string>,
    ) => {
      if (state.cart.products[payload.payload]) {
        state.cart.totalPrice -=
          parseFloat(state.cart.products[payload.payload].data.sell_price) *
          state.cart.products[payload.payload].count;
        delete state.cart.products[payload.payload];
        state.cart.product_ids = state.cart.product_ids.filter(
          (id) => id !== payload.payload,
        );
      }
    },
    clearExtra: (
      state: QuickAppointmentState,
      payload: PayloadAction<string>,
    ) => {
      if (state.cart.extras[payload.payload]) {
        state.cart.totalPrice -=
          parseFloat(state.cart.extras[payload.payload].data.price) *
          state.cart.extras[payload.payload].count;
        delete state.cart.extras[payload.payload];
        state.cart.extra_ids = state.cart.extra_ids.filter(
          (id) => id !== payload.payload,
        );
      }
    },
    clearStaff: (state: QuickAppointmentState) => {
      state.cart.staff = undefined;
    },
    resetCart: (state: QuickAppointmentState) => {
      state.cart = {
        services: {},
        products: {},
        extras: {},
        staff: undefined,
        service_ids: [],
        product_ids: [],
        extra_ids: [],
        totalPrice: 0,
      };
    },
  },
});

export const openSelector = (state: {
  quick_appointment: QuickAppointmentState;
}) => state.quick_appointment.open;

export const stepSelector = (state: {
  quick_appointment: QuickAppointmentState;
}) => state.quick_appointment.step;

export const categoriesSelector = (state: {
  quick_appointment: QuickAppointmentState;
}) => state.quick_appointment.categories;

export const servicesSelector = (state: {
  quick_appointment: QuickAppointmentState;
}) =>
  state.quick_appointment.services[state.quick_appointment.category_id ?? ''] ||
  [];

export const productsSelector = createSelector(
  [
    (state: { quick_appointment: QuickAppointmentState }) =>
      state.quick_appointment.products,
    (state: { quick_appointment: QuickAppointmentState }) =>
      state.quick_appointment.service_id,
  ],
  (products, service_id) => products[service_id ?? ''] || [],
);

export const staffsSelector = createSelector(
  [
    (state: { quick_appointment: QuickAppointmentState }) =>
      state.quick_appointment.staffs,
    (state: { quick_appointment: QuickAppointmentState }) =>
      state.quick_appointment.service_id,
  ],
  (staffs, service_id) => staffs[service_id ?? ''] || [],
);

export const selectActiveCategory = (state: {
  quick_appointment: QuickAppointmentState;
}) => state.quick_appointment.category_id;

export const selectActiveService = (state: {
  quick_appointment: QuickAppointmentState;
}) => state.quick_appointment.service_id;

export const cartSelector = (state: {
  quick_appointment: QuickAppointmentState;
}) => state.quick_appointment.cart;

export const selectCartServices = createSelector(cartSelector, (cart) => ({
  map: cart?.services,
  ids: cart?.service_ids,
}));

export const selectCartProducts = createSelector(cartSelector, (cart) => ({
  map: cart?.products,
  ids: cart?.product_ids,
}));

export const selectCartExtras = createSelector(cartSelector, (cart) => ({
  map: cart?.extras,
  ids: cart?.extra_ids,
}));

export const selectCartStaff = createSelector(
  cartSelector,
  (cart) => cart?.staff,
);

export const {
  setOpen,
  setStep,
  setInitialData,
  setActiveCategory,
  setActiveService,
  setService,
  setProduct,
  setExtra,
  setStaff,
  removeService,
  removeProduct,
  removeExtra,
  removeStaff,
  clearService,
  clearProduct,
  clearExtra,
  clearStaff,
  resetCart,
} = quickAppointment.actions;

export default quickAppointment.reducer;
